import { Chain, Currency, makeRequest } from "@paywithglide/glide-js";
import { GlideDepositConfigContextType } from "./useDepositConfig";
import {
  encodeFunctionData,
  erc20Abi,
  Hex,
  parseEther,
  parseUnits,
} from "viem";
import { logger } from "./logger";

export type useGlideTxOpts = {
  amount?: string;
  chain?: Chain;
  currency?: Currency;
  recipient?: string;
};

export const createGlideTx = async (
  { glideConfig, ...depositConfig }: GlideDepositConfigContextType,
  opts: useGlideTxOpts = {},
) => {
  if (depositConfig.mode === "call" && depositConfig.evmTx) {
    return {
      chainId: `eip155:${depositConfig.evmTx.chainId}` as const,
      evm: {
        address: depositConfig.evmTx.to,
        data: depositConfig.evmTx.data,
        value: depositConfig.evmTx.value,
        approval: depositConfig.tokenApproval,
      },
    } as const;
  }

  const amount = opts.amount || depositConfig.amount || "0";
  const chain = opts.chain || depositConfig.chain!;
  const currency = opts.currency || depositConfig.currency!;
  const recipient = opts.recipient || depositConfig.recipient || "0x0";
  const isTokenTransfer = Boolean(currency.contractAddressOn);

  logger.info(
    `[createGlideTx] Creating Glide transaction.. Chain ID: ${chain.id} Opts: ${JSON.stringify(
      {
        amount,
        chain: chain.id,
        currency: currency.id,
        recipient,
        isTokenTransfer,
      },
    )}`,
    {
      key: "createGlideTx",
      publish: true,
    },
  );

  if (chain.id.includes("solana")) {
    const { message } = await makeRequest<{
      message: string;
    }>(glideConfig, {
      url: "/svm/create-transfer-message",
      body: {
        currency: currency.on(chain),
        currencyDecimals: currency.decimals,
        recipient,
        amount,
      },
    });

    return {
      chainId: chain.id,
      solana: {
        message,
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    } as const;
  } else if (chain.id.includes("eip155") && isTokenTransfer) {
    return {
      chainId: chain.id,
      evm: {
        address: currency.contractAddressOn!(chain),
        data: encodeFunctionData({
          abi: erc20Abi,
          functionName: "transfer",
          args: [recipient as Hex, parseUnits(amount, currency.decimals)],
        }),
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    } as const;
  } else if (chain.id.includes("eip155")) {
    return {
      chainId: chain.id,
      evm: {
        address: recipient,
        value: parseEther(amount),
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    } as const;
  } else if (chain.id.includes("starknet") && isTokenTransfer) {
    return {
      chainId: chain.id,
      transfer: {
        token: currency.contractAddressOn!(chain),
        recipient,
        amount,
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    };
  } else if (chain.id.includes("hypercore") && isTokenTransfer) {
    // HyperCore uses EVM-compatible addresses and token format
    return {
      chainId: chain.id,
      evm: {
        address: currency.contractAddressOn!(chain),
        data: encodeFunctionData({
          abi: erc20Abi,
          functionName: "transfer",
          args: [recipient as Hex, parseUnits(amount, currency.decimals)],
        }),
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    } as const;
  } else if (chain.id.includes("hypercore")) {
    // HyperCore native token transfer
    return {
      chainId: chain.id,
      evm: {
        address: recipient,
        value: parseEther(amount),
      },
      gasRefuelAmount:
        depositConfig.gasRefuelAmountPerChain?.[chain.id] || undefined,
    } as const;
  }

  throw new Error("unsupported transaction");
};
